import type { SubPageContent } from "@/components/common/SubPageLayout";
import type { Lang } from "@/lib/i18n";

export const COPY: Record<Lang, SubPageContent> = {
  en: {
    breadcrumbKey: "GasLiquidSeparation",
    relatedTopic: "gas",
    hero: {
      categoryBadge: "Gas & Air Filtration",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Gas-Liquid Separation & Mist Elimination Wire Mesh",
      lead: "Engineered stainless steel knitted wire mesh demister pads, sintered porous laminates, and precision screen assemblies designed for high-efficiency liquid carryover removal, aerosol droplet coalescence, and process gas conditioning across scrubbers and separation vessels.",
      secondaryCta: {
        label: "Gas & Air Filtration",
        to: "/GasAndAirFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-knitted-sta-a66f45.png",
        alt: "Industrial stainless steel knitted mesh demister pad for gas liquid separation vessel",
      },
      cardNote: "All-metal construction & calibrated void fractions",
      cardLink: {
        label: "Request Technical Review",
        to: "/ContactPage",
      },
      specs: [
        { label: "Droplet Retention", value: "1 µm – 50 µm" },
        { label: "Face Velocity", value: "1.5 – 3.8 m/s" },
        { label: "Mesh Structure", value: "Knitted & Sintered" },
        { label: "Alloy Metallurgy", value: "AISI 304 / 316L" },
      ],
    },
    overview: {
      eyebrow: "Process Engineering",
      heading: "Inertial Impaction and Coalescence for High-Purity Gas De-Entrainment",
      paragraphs: [
        "Industrial processing columns, absorption towers, evaporators, and scrubber vessels routinely generate process gas streams bearing entrained liquid aerosols, condensed mists, and fine spray droplets. Unmitigated liquid carryover reduces thermal efficiency, contaminates downstream chemical catalysts, and induces mechanical erosion on high-speed rotating equipment such as gas compressors and blowers.",
        "MutualFilter supplies engineered all-metal gas-liquid separation media designed to capture and coalesce micro-droplets through targeted inertial impaction and direct interception. Structured knitted wire mesh pads create tortuous flow passages with high volumetric void fraction, enabling continuous liquid drainage while preserving low overall bed pressure drop across changing operating gas velocities.",
        "For sub-micron aerosol capture or high mechanical stress locations, our diffusion-bonded multilayer sintered composites provide rigid pore geometry and structural self-support. All separation elements are fabricated from passivated AISI 304 and 316L stainless steel, delivering reliable metallurgical integrity under corrosive vapors, organic solvents, and cyclic clean-in-place sanitation.",
      ],
      keyPoints: [
        "Inertial impaction and continuous filament coalescence capture liquid droplets across 1 µm to 50 µm",
        "High volumetric void fraction maintains minimal differential pressure across 1.5 to 3.8 m/s face velocity",
        "Passivated AISI 304 and 316L stainless steel alloys withstand aggressive vapors and routine steam or solvent cleaning",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-knitted-sta-a66f45.png",
        alt: "Stainless steel knitted wire mesh pad element for gas liquid mist elimination",
      },
    },
    specTable: {
      heading: "Media Configurations for Gas-Liquid Separation Equipment",
      intro: "Representative metallic filter and demister media configurations engineered for industrial scrubbers, knockout drums, and chemical contact columns.",
      columns: [
        { key: "service", label: "Separation Service", align: "left" },
        { key: "construction", label: "Media Construction", align: "left" },
        { key: "retention", label: "Target Droplet Range", align: "right" },
        { key: "alloys", label: "Standard Alloys", align: "right" },
        { key: "drainage", label: "Drainage Mechanism", align: "right" },
      ],
      rows: [
        {
          service: "Standard Scrubbers & Knockout Drums",
          construction: "Knitted wire mesh demister pad with top & bottom grid",
          retention: "5 µm – 50 µm",
          alloys: "AISI 304 / 316L",
          drainage: "Gravity drainage downward against rising gas",
        },
        {
          service: "Fine Aerosol Coalescence",
          construction: "Multi-density multi-filament knitted composite pad",
          retention: "2 µm – 10 µm",
          alloys: "AISI 316L",
          drainage: "Inter-layer coalescence & perimeter channels",
        },
        {
          service: "Severe Gas Scrubbing & High Dynamic Load",
          construction: "Diffusion-bonded 5-layer sintered mesh plate / cylinder",
          retention: "1 µm – 20 µm",
          alloys: "AISI 316L",
          drainage: "Capillary wick action & controlled drain slots",
        },
        {
          service: "High-Volume Intake Moisture Separation",
          construction: "Heavy-gauge woven wire cloth with welded stiffeners",
          retention: "20 µm – 100 µm",
          alloys: "AISI 304 / 316L",
          drainage: "Surface film runoff to sump collector",
        },
      ],
      caption: "All demister pad diameters, layer thicknesses, wire diameters, and support grid styles are manufactured to process vessel dimensions.",
    },
    features: {
      heading: "Key Engineering & Separation Advantages",
      intro: "Our stainless steel separation media provide process-proven droplet capture efficiency, structural rigidity, and cleanability for demanding gas treatment circuits.",
      items: [
        {
          title: "Tortuous Multi-Filament Path",
          text: "Interlocked knitted wire loops provide abundant impingement targets that force rapid momentum transfer and continuous droplet coalescence without flow stagnation.",
        },
        {
          title: "High Void Volume & Low Delta P",
          text: "Optimized open void geometry ensures free gas passage at operating velocities from 1.5 to 3.8 m/s, preventing unmanageable column pressure losses.",
        },
        {
          title: "Rigid Structural Reinforcement",
          text: "Welded top and bottom stainless steel tie-bars, support grids, and partition frames prevent pad dislodgement or bypass under process flow surges.",
        },
        {
          title: "Corrosion & Solvent Resistance",
          text: "High-grade passivated austenitic alloys resist chloride attack, acidic condensates, and harsh chemical vapors across prolonged continuous service.",
        },
      ],
    },
    related: {
      heading: "Related Separation Applications & Products",
      intro: "Explore complementary solid, liquid, and gas separation solutions engineered from precision stainless steel wire mesh.",
      cards: [
        {
          title: "Screening & Separation Hub",
          text: "Comprehensive metallic screening and separation media for particle classification, slurry dewatering, and process gas treatment.",
          to: "/ScreeningAndSeparationHubPage",
          badge: "Parent Application",
          linkLabel: "View Hub Overview",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sieve_bend_screen.webp",
            alt: "Screening and separation hub overview showing stainless steel separation media",
          },
        },
        {
          title: "Solid-Liquid Separation",
          text: "Continuous-slot wedge wire screens and heavy-duty woven mesh engineered for slurry dewatering, solids recovery, and industrial liquid clarification.",
          to: "/SolidLiquidSeparationPage",
          badge: "Sibling Application",
          linkLabel: "Explore Solid-Liquid",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-eaf99e.png",
            alt: "Stainless steel wedge wire screen panel for solid liquid separation",
          },
        },
        {
          title: "Powder & Particle Screening",
          text: "Calibrated square weave wire cloth and tensioned screen assemblies for high-frequency vibratory classification and fine dry powder sizing.",
          to: "/PowderAndParticleScreeningPage",
          badge: "Sibling Application",
          linkLabel: "Explore Powder Screening",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-83c744.png",
            alt: "Precision woven wire mesh screen for powder and particle classification",
          },
        },
        {
          title: "Woven Wire Mesh",
          text: "Precision square and Dutch weave wire cloth manufactured to ISO 9044 and ASTM E2016 for critical filtration, separation, and screening applications.",
          to: "/WovenWireMeshPage",
          badge: "Recommended Product",
          linkLabel: "Explore Woven Wire Mesh",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-83c744.png",
            alt: "Stainless steel woven wire mesh for industrial screening and filtration",
          },
        },
        {
          title: "Filter Components",
          text: "Custom-fabricated stainless steel demister assemblies, welded screen cylinders, and multi-layer filter elements for industrial separation vessels.",
          to: "/FilterComponentsPage",
          badge: "Recommended Product",
          linkLabel: "Explore Filter Components",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-an-industria-3baee0.png",
            alt: "Stainless steel filter tubes and cylinder screen components",
          },
        },
      ],
    },
    cta: {
      id: "gas-liquid-quote",
      eyebrow: "Engineering Consultation",
      heading: "Request a Custom Gas-Liquid Demister or Mesh Specification",
      lead: "Provide your target vessel diameter, droplet capture threshold, operating face velocity, alloy grade, and structural framing requirements for an engineering review and quotation.",
      secondaryLabel: "Gas & Air Filtration",
      secondaryTo: "/GasAndAirFiltrationPage",
      note: "Material test reports (MTR) and positive material identification (PMI) available when specified in the order.",
    },
  },
  zh: {
    breadcrumbKey: "GasLiquidSeparation",
    relatedTopic: "gas",
    hero: {
      categoryBadge: "气体与空气过滤",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "气液分离与丝网除沫技术",
      lead: "专为洗涤塔、吸收柱、蒸发器及分离容器设计制造的工程级不锈钢针织除沫网垫、烧结多孔复合网及精密分离组件，高效拦截夹带液滴、气溶胶雾沫，消除工艺气体带液并保护下游关键设备。",
      secondaryCta: {
        label: "气体与空气过滤",
        to: "/GasAndAirFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-knitted-sta-a66f45.png",
        alt: "用于气液分离容器的工业不锈钢针织丝网除沫器垫",
      },
      cardNote: "全金属刚性结构与精确校准的空隙率",
      cardLink: {
        label: "技术方案咨询",
        to: "/ContactPage",
      },
      specs: [
        { label: "捕集粒径", value: "1 µm – 50 µm" },
        { label: "设计气速", value: "1.5 – 3.8 m/s" },
        { label: "网面形态", value: "针织网垫 / 烧结复合网" },
        { label: "标准材质", value: "AISI 304 / 316L" },
      ],
    },
    overview: {
      eyebrow: "工艺工程解析",
      heading: "惯性碰撞与微沫凝聚机制 实现高纯度工艺气体脱湿分离",
      paragraphs: [
        "在石油化工、精细化工、天然气处理及环保废气洗涤工况中，塔器反应气与烟气气流往往夹带大量冷凝雾滴、酸性气溶胶与机械飞溅微沫。未加拦截的液体夹带不仅会降低热传导效率、中毒下游催化剂，还会对压缩机、风机等高速旋转设备造成严重的机械冲蚀与腐蚀损伤。",
        "MutualFilter 提供全金属气液分离介质，依托特种针织丝网垫与扩散焊接多孔复合层构建多弯曲折流通道。夹带液滴在曲折气流中因惯性力撞击丝线表面，并在金属微丝表面汇集、凝聚为大液滴，在重力作用下顺畅下落流向底部集液区，在稳定设计气速下实现低运行压降。",
        "针对严苛气流冲刷与亚微米级气溶胶拦截，我们提供多层烧结不锈钢刚性滤板及圆筒分离元件，孔隙稳定不变形。全系产品选用经严格酸洗钝化处理的 AISI 304 与 316L 奥氏体不锈钢制造，耐受酸碱烟气、有机溶剂蒸汽腐蚀及周期性蒸汽在线吹扫。",
      ],
      keyPoints: [
        "依托惯性碰撞拦截与金属微丝凝聚机制，精准捕集 1 µm 至 50 µm 范围内的悬浮微细雾滴",
        "科学优化三维网垫空隙率与比表面积配比，在 1.5 – 3.8 m/s 气速下维持极低床层压降",
        "选用酸洗钝化 AISI 304 与 316L 不锈钢，稳定耐受腐蚀性酸碱蒸汽冲刷与在线溶剂清洗",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-knitted-sta-a66f45.png",
        alt: "用于气液分离与除沫的不锈钢针织丝网元件结构",
      },
    },
    specTable: {
      heading: "气液分离设备常用滤材与除沫构件配置",
      intro: "以下列出适用于工业吸收塔、气液分离罐、洗涤塔及除沫器系统的典型金属过滤介质配置。",
      columns: [
        { key: "service", label: "应用分离工况", align: "left" },
        { key: "construction", label: "核心构型与介质形式", align: "left" },
        { key: "retention", label: "目标捕集粒径", align: "right" },
        { key: "alloys", label: "推荐合金材质", align: "right" },
        { key: "drainage", label: "液体排液沉降方式", align: "right" },
      ],
      rows: [
        {
          service: "常规洗涤塔与分离罐除沫",
          construction: "标准针织丝网除沫网垫（配上下加固格栅）",
          retention: "5 µm – 50 µm",
          alloys: "AISI 304 / 316L",
          drainage: "重力自流逆向排液",
        },
        {
          service: "微细气溶胶碰撞凝聚",
          construction: "多密度梯度复合针织丝网垫",
          retention: "2 µm – 10 µm",
          alloys: "AISI 316L",
          drainage: "层间凝聚自流与周边导流槽",
        },
        {
          service: "高压差与严苛气流冲刷工况",
          construction: "扩散焊接 5 层烧结不锈钢滤板 / 滤筒",
          retention: "1 µm – 20 µm",
          alloys: "AISI 316L",
          drainage: "微孔毛细导流与底部排液槽",
        },
        {
          service: "大风量进气粗滴与雨雾截留",
          construction: "重载不锈钢平纹编织网（配焊接加强筋）",
          retention: "20 µm – 100 µm",
          alloys: "AISI 304 / 316L",
          drainage: "液膜附着汇集流向集液槽",
        },
      ],
      caption: "各项除沫垫直径、厚度、丝径及支撑格栅形式均可根据容器工艺图纸与气速参数定制加工。",
    },
    features: {
      heading: "关键工程技术与运行优势",
      intro: "我们的不锈钢气液分离与除沫介质兼具高效液滴截留能力、刚性承重骨架与优良的抗化学腐蚀性能。",
      items: [
        {
          title: "曲折多向丝网交织通道",
          text: "互锁波浪针织微丝形成立体网状结构，为流动气体提供密集惯性碰撞靶标，促进微沫迅速凝聚且不造成气流堵塞。",
        },
        {
          title: "超高空隙率与低运行阻力",
          text: "精准调配的容积开孔率保证工艺气体在 1.5 至 3.8 m/s 常用工况速度下畅通通过，有效规避不必要的床层能耗水头损失。",
        },
        {
          title: "刚性支撑与抗冲刷骨架",
          text: "配备不锈钢焊接上下格栅条、围边扁钢与分块加强连接件，在气流脉冲冲击下防止网垫塌陷或气体侧旁短路。",
        },
        {
          title: "耐酸碱与抗有机介质腐蚀",
          text: "钝化处理的优质奥氏体不锈钢稳定耐受硫化物、氯化物弱酸蒸汽及工业烃类挥发物，长周期连续运行不易发生孔蚀。",
        },
      ],
    },
    related: {
      heading: "相关分离应用与推荐产品",
      intro: "浏览 MutualFilter 围绕工业分级、固液澄清与流体净化打造的一体化金属丝网构件方案。",
      cards: [
        {
          title: "筛分与分离总览",
          text: "涵盖颗粒精细分级、矿浆连续脱水与工艺气相除沫的全金属分离介质综合方案。",
          to: "/ScreeningAndSeparationHubPage",
          badge: "父级应用分类",
          linkLabel: "查看总览方案",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sieve_bend_screen.webp",
            alt: "筛分与分离中心总览不锈钢分离介质",
          },
        },
        {
          title: "固液分离与脱水",
          text: "连续缝隙自释形楔形丝筛网与重载不锈钢编织网，专为矿浆脱水、杂质截留及流体澄清设计。",
          to: "/SolidLiquidSeparationPage",
          badge: "同级应用",
          linkLabel: "探索固液分离",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-eaf99e.png",
            alt: "展示高精度连续缝隙截面的不锈钢楔形丝脱水筛板",
          },
        },
        {
          title: "粉体与颗粒筛分",
          text: "精密方孔编织丝网与预张紧筛网组件，适配高频振动筛分机与超声波微细物料分选系统。",
          to: "/PowderAndParticleScreeningPage",
          badge: "同级应用",
          linkLabel: "探索粉体筛分",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-83c744.png",
            alt: "精密粉体与颗粒分级不锈钢编织丝网结构细节",
          },
        },
        {
          title: "不锈钢编织丝网",
          text: "严格遵循 ISO 9044 与 ASTM E2016 标准制造的精密方孔网与密纹编织网，适用于精密拦截与分级。",
          to: "/WovenWireMeshPage",
          badge: "推荐产品",
          linkLabel: "探索编织丝网",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-83c744.png",
            alt: "用于工业筛分与精密过滤的不锈钢编织丝网",
          },
        },
        {
          title: "过滤组件",
          text: "定制焊接与烧结除沫筒、精密滤筒、复合滤板及支撑框架构件，匹配各类严苛工艺容器。",
          to: "/FilterComponentsPage",
          badge: "推荐产品",
          linkLabel: "探索过滤组件",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-an-industria-3baee0.png",
            alt: "不锈钢焊接与烧结圆筒滤筒滤网构件",
          },
        },
      ],
    },
    cta: {
      id: "gas-liquid-quote",
      eyebrow: "定制方案咨询",
      heading: "获取气液分离除沫网垫与金属滤网技术报价",
      lead: "请提供您的容器内径、设计气速、截留雾滴范围、材质牌号及格栅固定要求，我们的工程技术团队将为您核算适用的除沫方案并出具报价。",
      secondaryLabel: "气体与空气过滤",
      secondaryTo: "/GasAndAirFiltrationPage",
      note: "材质报告 (MTR) 与化学成分检测 (PMI) 可按订单要求提供。",
    },
  },
  es: {
    breadcrumbKey: "GasLiquidSeparation",
    relatedTopic: "gas",
    hero: {
      categoryBadge: "Filtración de gases y aire",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Separación Gas-Líquido y Eliminación de Niebla por Malla Metálica",
      lead: "Eliminadores de niebla en malla tejida de acero inoxidable, laminados porosos sinterizados y conjuntos filtrantes de precisión para retener arrastres de líquido, coalescer gotas de aerosol y acondicionar gases de proceso en lavadores y columnas de separación.",
      secondaryCta: {
        label: "Filtración de gases y aire",
        to: "/GasAndAirFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-knitted-sta-a66f45.png",
        alt: "Separador de gotas de malla tejida de acero inoxidable para recipiente de separación gas-líquido",
      },
      cardNote: "Construcción totalmente metálica y fracción de huecos calibrada",
      cardLink: {
        label: "Consulta Técnica",
        to: "/ContactPage",
      },
      specs: [
        { label: "Retención de Gotas", value: "1 µm – 50 µm" },
        { label: "Velocidad del Gas", value: "1,5 – 3,8 m/s" },
        { label: "Estructura de Malla", value: "Tricotada y Sinterizada" },
        { label: "Aleaciones Metálicas", value: "AISI 304 / 316L" },
      ],
    },
    overview: {
      eyebrow: "Ingeniería de Procesos",
      heading: "Impacto Inercial y Coalescencia para Separación de Alta Pureza Gas-Líquido",
      paragraphs: [
        "Las columnas de proceso químico, torres de absorción, evaporadores y lavadores de gases generan de forma continua corrientes de gas que arrastran gotas de condensado y nieblas finas. La presencia de líquido arrastrado deteriora el rendimiento térmico, contamina catalizadores y provoca desgaste mecánico en compresores y turbosoplantes.",
        "MutualFilter fabrica medios metálicos de separación gas-líquido diseñados para capturar y coalescer microgotas mediante impacto inercial e intercepción directa. Las matelas de malla tricotada generan trayectorias sinuosas con alta porosidad volumétrica, permitiendo el drenaje continuo del líquido acumulado con una pérdida de carga mínima en las velocidades de diseño.",
        "Para aerosoles submicrónicos o condiciones con altos esfuerzos dinámicos, suministramos compuestos sinterizados multicapa con estructura porosa rígida y autoportante. Todos los elementos se fabrican en aceros inoxidables pasivados AISI 304 y 316L, ofreciendo alta resistencia a vapores corrosivos y lavajes químicos de rutina.",
      ],
      keyPoints: [
        "El impacto inercial y la coalescencia continua capturan gotas líquidas de 1 µm a 50 µm",
        "Alta fracción de huecos que mantiene baja pérdida de carga a velocidades superficiales de 1,5 a 3,8 m/s",
        "Aceros inoxidables AISI 304 y 316L pasivados resistentes a vapores corrosivos y limpiezas químicas",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-knitted-sta-a66f45.png",
        alt: "Matela desnebulizadora de malla de alambre de acero inoxidable para separación gas-líquido",
      },
    },
    specTable: {
      heading: "Configuraciones de Medios para Equipos de Separación Gas-Líquido",
      intro: "Medios metálicos y eliminadores de niebla típicos diseñados para lavadores industriales, depósitos de separación y columnas de contacto.",
      columns: [
        { key: "service", label: "Servicio de Separación", align: "left" },
        { key: "construction", label: "Construcción del Medio", align: "left" },
        { key: "retention", label: "Rango de Gotas Objetivo", align: "right" },
        { key: "alloys", label: "Aleaciones Estándar", align: "right" },
        { key: "drainage", label: "Mecanismo de Drenaje", align: "right" },
      ],
      rows: [
        {
          service: "Lavadores Estándar y Separadores Knockout",
          construction: "Matela demister de malla tricotada con rejilla superior e inferior",
          retention: "5 µm – 50 µm",
          alloys: "AISI 304 / 316L",
          drainage: "Drenaje por gravedad a contracorriente",
        },
        {
          service: "Coalescencia de Aerosoles Finos",
          construction: "Matela compuesta multifilamento de densidad graduada",
          retention: "2 µm – 10 µm",
          alloys: "AISI 316L",
          drainage: "Coalescencia interlaminar y canales perimetrales",
        },
        {
          service: "Lavado Severo y Alta Carga Dinámica",
          construction: "Placa o cilindro sinterizado de 5 capas unido por difusión",
          retention: "1 µm – 20 µm",
          alloys: "AISI 316L",
          drainage: "Efecto capilar y ranuras controladas de purga",
        },
        {
          service: "Separación de Lluvia y Gotas Gruesas en Admisión",
          construction: "Tela metálica de tejido cuadrado pesado con refuerzos",
          retention: "20 µm – 100 µm",
          alloys: "AISI 304 / 316L",
          drainage: "Escurrimiento laminar hacia colector inferior",
        },
      ],
      caption: "Diámetros, espesores, calibres de alambre y rejillas de sujeción se fabrican según especificaciones del recipiente.",
    },
    features: {
      heading: "Ventajas Operativas y de Fabricación",
      intro: "Nuestros medios de separación combinan retención eficaz de gotas, rigidez mecánica y resistencia química en operaciones continuas.",
      items: [
        {
          title: "Trayectoria Sinuosa Multifilamento",
          text: "Mallas entrelazadas que ofrecen múltiples dianas de impacto inercial, facilitando la coalescencia de microgotas sin provocar colapso de flujo.",
        },
        {
          title: "Elevada Porosidad y Mínimo Delta P",
          text: "Fracción de volumen libre calibrada para permitir el paso fluido de gas entre 1,5 y 3,8 m/s, evitando caídas de presión excesivas.",
        },
        {
          title: "Refuerzo Estructural Rígido",
          text: "Rejillas soldadas superiores e inferiores que evitan desplazamientos, deformaciones o fugas perimetrales bajo pulsos de caudal.",
        },
        {
          title: "Resistencia a la Corrosión",
          text: "Aceros austeníticos pasivados que toleran vapores ácidos, condensados clorados y limpiezas CIP alcalinas sin deterioro prematuro.",
        },
      ],
    },
    related: {
      heading: "Aplicaciones y Productos Relacionados",
      intro: "Explore soluciones complementarias en tamizado, clarificación líquida y medios de separación de acero inoxidable.",
      cards: [
        {
          title: "Tamizado y Separación",
          text: "Soluciones globales en mallas metálicas para clasificación de partículas, deshidratación y tratamiento de gases.",
          to: "/ScreeningAndSeparationHubPage",
          badge: "Aplicación Principal",
          linkLabel: "Ver Visión General",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sieve_bend_screen.webp",
            alt: "Visión general de separación y tamizado con medios metálicos",
          },
        },
        {
          title: "Separación Sólido-Líquido",
          text: "Cribas de alambre en cuña y telas tejidas pesadas para deshidratación de lodos y clarificación de fluidos de proceso.",
          to: "/SolidLiquidSeparationPage",
          badge: "Aplicación Hermana",
          linkLabel: "Explorar Sólido-Líquido",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-eaf99e.png",
            alt: "Panel de cribado de deshidratación en alambre en cuña de acero inoxidable",
          },
        },
        {
          title: "Tamizado de Polvos y Partículas",
          text: "Mallas de tejido cuadrado de precisión y marcos tensados para cribado vibratorio de alta frecuencia.",
          to: "/PowderAndParticleScreeningPage",
          badge: "Aplicación Hermana",
          linkLabel: "Explorar Tamizado de Polvos",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-83c744.png",
            alt: "Malla de alambre de acero inoxidable para clasificación de partículas y polvos",
          },
        },
        {
          title: "Mallas de Alambre Tejidas",
          text: "Telas metálicas de precisión fabricadas según ISO 9044 y ASTM E2016 para aplicaciones críticas de filtración.",
          to: "/WovenWireMeshPage",
          badge: "Producto Recomendado",
          linkLabel: "Explorar Mallas Tejidas",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-83c744.png",
            alt: "Malla de alambre tejida de acero inoxidable para tamizado y filtración industrial",
          },
        },
        {
          title: "Componentes Filtrantes",
          text: "Eliminadores de niebla cilíndricos, tubos soldados y conjuntos multicapa fabricados a medida para recipientes de proceso.",
          to: "/FilterComponentsPage",
          badge: "Producto Recomendado",
          linkLabel: "Explorar Componentes",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-an-industria-3baee0.png",
            alt: "Tubos y cilindros filtrantes de acero inoxidable soldados y sinterizados",
          },
        },
      ],
    },
    cta: {
      id: "gas-liquid-quote",
      eyebrow: "Consulta Técnica",
      heading: "Solicite Cotización para Eliminadores de Niebla y Mallas Gas-Líquido",
      lead: "Indique el diámetro del depósito, rango de gotas a retener, velocidad del gas, aleación y detalles de fijación estructural para una revisión técnica.",
      secondaryLabel: "Filtración de gases y aire",
      secondaryTo: "/GasAndAirFiltrationPage",
      note: "Informes de ensayo de material (MTR) y análisis PMI disponibles cuando se especifiquen en el pedido.",
    },
  },
  de: {
    breadcrumbKey: "GasLiquidSeparation",
    relatedTopic: "gas",
    hero: {
      categoryBadge: "Gas- und Luftfiltration",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Gas-Flüssig-Trennung & Drahtgewebe-Tropfenabscheidung",
      lead: "Hochwertige Tropfenabscheiderkissen aus Edelstahl-Drahtgestrick, poröse Sinterverbunde und maßgeschneiderte Trennelemente zur zuverlässigen Beseitigung von Flüssigkeitsmitriss, Aerosolkoaleszenz und Prozessgaskonditionierung in Wäschern und Abscheidekolonnen.",
      secondaryCta: {
        label: "Gas- und Luftfiltration",
        to: "/GasAndAirFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-knitted-sta-a66f45.png",
        alt: "Edelstahl-Drahtgestrick-Tropfenabscheider für Gas-Flüssig-Trennbehälter",
      },
      cardNote: "Ganzmetall-Konstruktion & kalibriertes Porenvolumen",
      cardLink: {
        label: "Technische Beratung",
        to: "/ContactPage",
      },
      specs: [
        { label: "Tropfenabscheidung", value: "1 µm – 50 µm" },
        { label: "Anströmgeschw.", value: "1,5 – 3,8 m/s" },
        { label: "Gewebestruktur", value: "Gestrickt & Gesintert" },
        { label: "Werkstoffe", value: "AISI 304 / 316L" },
      ],
    },
    overview: {
      eyebrow: "Verfahrenstechnik",
      heading: "Trägheitsabscheidung und Koaleszenz für hochreine Gas-Flüssig-Trennung",
      paragraphs: [
        "In Kolonnen, Verdampfern, Absorbern und Gaswäschern führen Prozessgasströme kontinuierlich mitgerissene Nebeltröpfchen und Aerosole mit sich. Unkontrollierter Flüssigkeitsmitriss senkt den thermischen Wirkungsgrad, deaktiviert nachgeschaltete Katalysatoren und verursacht mechanische Schaufelerosion an Verdichtern und Gebläsen.",
        "MutualFilter liefert metallische Trennmedien zur Abscheidung und Koaleszenz von Mikrotropfen durch Trägheitsimpaktion und direkten Abfang. Gestrickte Drahtkissen bilden gewundene Strömungskanäle mit hohem Hohlraumanteil, die einen kontinuierlichen Flüssigkeitsablauf bei minimalem Druckverlust über den gesamten Auslegungsgeschwindigkeitsbereich sicherstellen.",
        "Für Submikron-Aerosole oder hohe dynamische Strömungsbelastungen bieten diffusionsgebundene Sinterverbunde formstabile, selbsttragende Porenstrukturen. Alle Elemente werden aus passiviertem Edelstahl AISI 304 und 316L hergestellt und widerstehen korrosiven Dämpfen, Lösungsmitteln sowie zyklischen CIP-Spülungen.",
      ],
      keyPoints: [
        "Trägheitsabscheidung und Drahtkoaleszenz fangen Flüssigkeitstropfen von 1 µm bis 50 µm ab",
        "Hohes Porenvolumen sichert niedrigen Differenzdruck bei 1,5 bis 3,8 m/s Anströmgeschwindigkeit",
        "Passivierte Werkstoffe AISI 304 und 316L halten aggressiven Dämpfen und Heißdampfreinigungen stand",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-knitted-sta-a66f45.png",
        alt: "Drahtgestrick-Tropfenabscheiderelement aus Edelstahl zur Tropfenabscheidung",
      },
    },
    specTable: {
      heading: "Filtermedien-Konfigurationen für die Gas-Flüssig-Trennung",
      intro: "Repräsentative Demister- und Filtermedien für industrielle Gaswäscher, Abscheidebehälter und Kolonnen.",
      columns: [
        { key: "service", label: "Trennaufgabe", align: "left" },
        { key: "construction", label: "Medienaufbau", align: "left" },
        { key: "retention", label: "Tropfenspektrum", align: "right" },
        { key: "alloys", label: "Standard-Werkstoffe", align: "right" },
        { key: "drainage", label: "Ablaufmechanismus", align: "right" },
      ],
      rows: [
        {
          service: "Standard-Wäscher & Knockout-Behälter",
          construction: "Gestricktes Drahtgewebe-Kissen mit oberem & unterem Traggitter",
          retention: "5 µm – 50 µm",
          alloys: "AISI 304 / 316L",
          drainage: "Schwerkraftablauf entgegen der Gasströmung",
        },
        {
          service: "Feinaerosol-Koaleszenz",
          construction: "Mehrlagiges Gestrickkissen mit Dichtegradient",
          retention: "2 µm – 10 µm",
          alloys: "AISI 316L",
          drainage: "Zwischenlagen-Koaleszenz & Ablaufkanäle",
        },
        {
          service: "Aggressive Dämpfe & hohe Strömungslast",
          construction: "Diffusionsgebundene 5-Lagen-Sinterplatte / Sinterzylinder",
          retention: "1 µm – 20 µm",
          alloys: "AISI 316L",
          drainage: "Kapillarabzug & gezielte Entwässerungsschlitze",
        },
        {
          service: "Grobtropfenabscheidung im Lufteinlass",
          construction: "Schweres Quadratmaschen-Drahtgewebe mit Aussteifungen",
          retention: "20 µm – 100 µm",
          alloys: "AISI 304 / 316L",
          drainage: "Flüssigkeitsfilmablauf in Sammelwanne",
        },
      ],
      caption: "Durchmesser, Kissenstärken, Drahtstärken und Tragrahmen werden an Behältermaße angepasst.",
    },
    features: {
      heading: "Betriebliche & Konstruktive Vorteile",
      intro: "Unsere Tropfenabscheider verbinden hohe Abscheidegrade, mechanische Stabilität und chemische Dauerbeständigkeit.",
      items: [
        {
          title: "Vielschichtig gewundener Strömungsweg",
          text: "Ineinandergreifende Drahtmaschen bieten dichte Prallflächen für wirkungsvollen Impulstransfer und Tropfenwachstum ohne Stauzonen.",
        },
        {
          title: "Hohes Porenvolumen & geringer Druckverlust",
          text: "Optimierte Hohlraumstruktur erlaubt ungehinderten Gasdurchtritt bei 1,5 bis 3,8 m/s ohne unwirtschaftliche Druckverluste.",
        },
        {
          title: "Stabile Tragrahmenkonstruktion",
          text: "Verschweißte Ober- und Untergitter fixieren das Drahtkissen zuverlässig gegen Druckstöße und verhindern Randbypässe.",
        },
        {
          title: "Korrosions- & Lösungsmittelfestigkeit",
          text: "Passivierte austenitische Edelstähle trotzen chloridhaltigen Kondensaten und sauren Dämpfen im Dauerbetrieb.",
        },
      ],
    },
    related: {
      heading: "Verwandte Trennanwendungen & Produkte",
      intro: "Entdecken Sie ergänzende Lösungen für Fest-Flüssig-Trennung, Pulversiebung und Edelstahl-Drahtgewebe.",
      cards: [
        {
          title: "Siebung & Trennung",
          text: "Ganzheitliche metallische Trennmedien für Partikelklassierung, Schlammentwässerung und Tropfenabscheidung.",
          to: "/ScreeningAndSeparationHubPage",
          badge: "Hauptanwendung",
          linkLabel: "Übersicht ansehen",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sieve_bend_screen.webp",
            alt: "Siebung und Trennung Übersicht mit Edelstahl-Filtermedien",
          },
        },
        {
          title: "Fest-Flüssig-Trennung",
          text: "Kontinuierliche Spaltsiebe und schwere Drahtgewebe für Schlammentwässerung und industrielle Klärung.",
          to: "/SolidLiquidSeparationPage",
          badge: "Geschwisteranwendung",
          linkLabel: "Fest-Flüssig entdecken",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-eaf99e.png",
            alt: "Edelstahl-Spaltsiebplatte zur Entwässerung",
          },
        },
        {
          title: "Pulver- & Partikelsiebung",
          text: "Präzisions-Quadratmaschengewebe und Siebrahmen für Vibrations- und Ultraschallsiebung feiner Pulver.",
          to: "/PowderAndParticleScreeningPage",
          badge: "Geschwisteranwendung",
          linkLabel: "Pulversiebung entdecken",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-83c744.png",
            alt: "Edelstahl-Drahtgewebe für die Pulver- und Partikelklassierung",
          },
        },
        {
          title: "Edelstahl-Drahtgewebe",
          text: "Präzisionsgewebe nach ISO 9044 und ASTM E2016 für anspruchsvolle Filtrations- und Abscheideprozesse.",
          to: "/WovenWireMeshPage",
          badge: "Empfohlenes Produkt",
          linkLabel: "Drahtgewebe entdecken",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-83c744.png",
            alt: "Edelstahl-Drahtgewebe für industrielle Siebung und Filtration",
          },
        },
        {
          title: "Filterkomponenten",
          text: "Maßgefertigte Tropfenabscheiderzylinder, geschweißte Filterrohre und Mehrlagenfilter für Abscheideanlagen.",
          to: "/FilterComponentsPage",
          badge: "Empfohlenes Produkt",
          linkLabel: "Komponenten entdecken",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-an-industria-3baee0.png",
            alt: "Geschweißte und gesinterte Filterrohre aus Edelstahl",
          },
        },
      ],
    },
    cta: {
      id: "gas-liquid-quote",
      eyebrow: "Technische Beratung",
      heading: "Angebot für Tropfenabscheider & Gas-Flüssig-Medien anfordern",
      lead: "Nennen Sie Behälterdurchmesser, Tropfenspektrum, Anströmgeschwindigkeit, Werkstoff und Einbauanforderungen für eine technische Auslegung.",
      secondaryLabel: "Gas- und Luftfiltration",
      secondaryTo: "/GasAndAirFiltrationPage",
      note: "Werksprüfzeugnisse (MTR) und PMI-Werkstoffanalysen auf Anfrage gemäß Auftragsvereinbarung verfügbar.",
    },
  },
  fr: {
    breadcrumbKey: "GasLiquidSeparation",
    relatedTopic: "gas",
    hero: {
      categoryBadge: "Filtration des gaz et de l'air",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Séparation Gaz-Liquide et Élimination des Brouillards par Toile Métallique",
      lead: "Matelas dévésiculeurs en tricot métallique inox, composites poreux frittés et ensembles filtrants de précision conçus pour éliminer l'entraînement de liquide, assurer la coalescence des aérosols et conditionner les gaz de procédé dans les colonnes et laveurs.",
      secondaryCta: {
        label: "Filtration des gaz et de l'air",
        to: "/GasAndAirFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-knitted-sta-a66f45.png",
        alt: "Matelas dévésiculeur en tricot métallique inox pour séparateur gaz-liquide",
      },
      cardNote: "Conception tout métal et porosité volumique calibrée",
      cardLink: {
        label: "Consultation Technique",
        to: "/ContactPage",
      },
      specs: [
        { label: "Rétention de Gouttes", value: "1 µm – 50 µm" },
        { label: "Vitesse Frontale", value: "1,5 – 3,8 m/s" },
        { label: "Structure Métallique", value: "Tricoté & Fritté" },
        { label: "Nuances d'Alliages", value: "AISI 304 / 316L" },
      ],
    },
    overview: {
      eyebrow: "Génie des Procédés",
      heading: "Impact Inertiel et Coalescence pour une Séparation Gaz-Liquide Haute Pureté",
      paragraphs: [
        "Dans les colonnes d'absorption, évaporateurs et tours de lavage industrielles, les gaz de procédé entraînent régulièrement des micro-gouttelettes et des brouillards condensés. Cet entraînement liquide nuit au rendement thermique, désactive les catalyseurs et entraîne l'érosion mécanique des turbines et compresseurs.",
        "MutualFilter conçoit des médias de séparation gaz-liquide tout métal assurant la capture et la coalescence des gouttelettes par impact inertiel et interception directe. Les matelas en fil tricoté créent des chemins d'écoulement tortueux avec une fraction de vide élevée, permettant un drainage régulier du liquide avec une perte de charge minime aux vitesses de consigne.",
        "Pour les aérosols submicroniques ou les environnements à fortes contraintes mécaniques, nos structures frittées multicouches offrent une porosité rigide indéformable. Réalisés en inox passivé AISI 304 et 316L, nos ensembles résistent aux vapeurs corrosives, aux solvants et aux lavages périodiques en place.",
      ],
      keyPoints: [
        "L'impact inertiel et la coalescence continue capturent les gouttelettes liquides de 1 µm à 50 µm",
        "Une forte fraction de vide assure une faible perte de charge à des vitesses superficielles de 1,5 à 3,8 m/s",
        "Nuances passivées AISI 304 et 316L résistant aux vapeurs agressives et aux nettoyages à la vapeur",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-knitted-sta-a66f45.png",
        alt: "Matelas dévésiculeur en tricot métallique en acier inoxydable pour séparation gaz-liquide",
      },
    },
    specTable: {
      heading: "Configurations de Médias pour Équipements de Séparation Gaz-Liquide",
      intro: "Médias métalliques types pour dévésiculeurs industriels, colonnes de lavage et ballons séparateurs.",
      columns: [
        { key: "service", label: "Procédé de Séparation", align: "left" },
        { key: "construction", label: "Construction du Média", align: "left" },
        { key: "retention", label: "Plage de Gouttes Visée", align: "right" },
        { key: "alloys", label: "Alliages Standards", align: "right" },
        { key: "drainage", label: "Mode de Drainage", align: "right" },
      ],
      rows: [
        {
          service: "Laveurs Standards & Ballons Séparateurs",
          construction: "Matelas dévésiculeur tricoté avec grilles supérieure et inférieure",
          retention: "5 µm – 50 µm",
          alloys: "AISI 304 / 316L",
          drainage: "Drainage gravitaire à contre-courant du gaz",
        },
        {
          service: "Coalescence d'Aérosols Fins",
          construction: "Matelas composite multifilament à gradient de densité",
          retention: "2 µm – 10 µm",
          alloys: "AISI 316L",
          drainage: "Coalescence intercouche et rigoles périphériques",
        },
        {
          service: "Gaz Corrosifs & Forte Sollicitation Dynamique",
          construction: "Panneau ou cylindre fritté 5 couches lié par diffusion",
          retention: "1 µm – 20 µm",
          alloys: "AISI 316L",
          drainage: "Action capillaire et fentes calibrées de purge",
        },
        {
          service: "Séparation d'Eau de Pluie & Grosses Gouttes",
          construction: "Toile métallique à maille carrée lourde avec renforts soudés",
          retention: "20 µm – 100 µm",
          alloys: "AISI 304 / 316L",
          drainage: "Ruissellement de film liquide vers puisard",
        },
      ],
      caption: "Diamètres, épaisseurs de matelas et grilles de maintien fabriqués sur mesure selon les dimensions de la virole.",
    },
    features: {
      heading: "Atouts Fonctionnels et de Fabrication",
      intro: "Nos toiles et dévésiculeurs combinent haute efficacité de captage, rigidité structurelle et tenue à la corrosion chimique.",
      items: [
        {
          title: "Parcours Tortueux Multifilament",
          text: "Boucles de fil tricoté imbriquées créant de multiples cibles d'impact pour accélérer la coalescence sans perte de débit.",
        },
        {
          title: "Fort Volume de Vide & Faible Delta P",
          text: "Porosité calibrée permettant le passage fluide des gaz à des vitesses de 1,5 à 3,8 m/s sans consommation excessive d'énergie.",
        },
        {
          title: "Armature Structurelle Rigide",
          text: "Grilles soudées supérieures et inférieures maintenant fermement le matelas contre les à-coups de pression sans passage préférentiel.",
        },
        {
          title: "Résistance Chimique et aux Solvants",
          text: "Inox austénitiques passivés supportant les condensats chlorés et les vapeurs de solvants sur de longues durées de service.",
        },
      ],
    },
    related: {
      heading: "Applications et Produits Associés",
      intro: "Découvrez nos solutions complémentaires en tamisage, séparation solide-liquide et toiles métalliques en acier inoxydable.",
      cards: [
        {
          title: "Criblage & Séparation",
          text: "Solutions globales en toiles métalliques pour le calibrage de particules, l'égouttage de boues et le traitement de gaz.",
          to: "/ScreeningAndSeparationHubPage",
          badge: "Application Principale",
          linkLabel: "Voir la Présentation",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sieve_bend_screen.webp",
            alt: "Présentation générale du criblage et de la séparation",
          },
        },
        {
          title: "Séparation Solide-Liquide",
          text: "Grilles à fil profilé et toiles tissées lourdes conçues pour l'égouttage des boues et la clarification industrielle.",
          to: "/SolidLiquidSeparationPage",
          badge: "Application Associée",
          linkLabel: "Explorer Solide-Liquide",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-eaf99e.png",
            alt: "Panneau d'égouttage à fil profilé en acier inoxydable",
          },
        },
        {
          title: "Tamisage de Poudres et Particules",
          text: "Toiles métalliques à mailles carrées et cadres sous tension pour le tamisage vibratoire haute fréquence.",
          to: "/PowderAndParticleScreeningPage",
          badge: "Application Associée",
          linkLabel: "Explorer le Tamisage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-83c744.png",
            alt: "Toile métallique inox pour la séparation et le tamisage de poudres",
          },
        },
        {
          title: "Toiles Métalliques Tissées",
          text: "Toiles de précision conformes à ISO 9044 et ASTM E2016 pour les applications critiques de filtration.",
          to: "/WovenWireMeshPage",
          badge: "Produit Recommandé",
          linkLabel: "Explorer les Toiles Tissées",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-83c744.png",
            alt: "Toile métallique inox pour filtration et criblage industriel",
          },
        },
        {
          title: "Composants Filtrants",
          text: "Cartouches dévésiculeuses, cylindres soudés et éléments multicouches configurés sur mesure pour colonnes industrielles.",
          to: "/FilterComponentsPage",
          badge: "Produit Recommandé",
          linkLabel: "Explorer les Composants",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-an-industria-3baee0.png",
            alt: "Tubes et cylindres filtrants en acier inoxydable soudés et frittés",
          },
        },
      ],
    },
    cta: {
      id: "gas-liquid-quote",
      eyebrow: "Étude Technique",
      heading: "Demandez un Devis pour Dévésiculeurs et Médias Gaz-Liquide",
      lead: "Précisez le diamètre de l'appareil, le seuil de coupure visé, la vitesse frontale, la nuance d'acier et les brides d'appui pour étude technique.",
      secondaryLabel: "Filtration des gaz et de l'air",
      secondaryTo: "/GasAndAirFiltrationPage",
      note: "Certificats matière (MTR) et contrôles PMI disponibles sur demande selon spécifications de la commande.",
    },
  },
  pt: {
    breadcrumbKey: "GasLiquidSeparation",
    relatedTopic: "gas",
    hero: {
      categoryBadge: "Filtração de gás e ar",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Separação Gás-Líquido e Eliminação de Névoa por Malha Metálica",
      lead: "Almofadas eliminadoras de névoa em malha tricotada de aço inoxidável, compósitos porosos sinterizados e conjuntos filtrantes de precisão para retenção de arraste líquido, coalescência de gotículas e condicionamento de gases de processo em lavadores e vasos separadores.",
      secondaryCta: {
        label: "Filtração de gás e ar",
        to: "/GasAndAirFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-knitted-sta-a66f45.png",
        alt: "Eliminador de névoa em malha tricotada de aço inoxidável para vaso de separação gás-líquido",
      },
      cardNote: "Construção toda em metal e fração de vazios calibrada",
      cardLink: {
        label: "Consulta Técnica",
        to: "/ContactPage",
      },
      specs: [
        { label: "Retenção de Gotas", value: "1 µm – 50 µm" },
        { label: "Velocidade do Gás", value: "1,5 – 3,8 m/s" },
        { label: "Estrutura Metálica", value: "Tricotada & Sinterizada" },
        { label: "Ligas Metálicas", value: "AISI 304 / 316L" },
      ],
    },
    overview: {
      eyebrow: "Engenharia de Processos",
      heading: "Impacto Inercial e Coalescência para Separação Gás-Líquido de Alta Eficiência",
      paragraphs: [
        "Colunas de contato químico, vasos evaporadores, torres de absorção e lavadores industriais geram continuamente correntes gasosas com arraste de névoas e microgotas condensadas. O arraste de líquido reduz o rendimento térmico, desativa catalisadores químicos e causa desgaste mecânico em compressores e ventiladores de processo.",
        "A MutualFilter projeta meios de separação metálicos para capturar e coalescer microgotas através de impacto inercial e interceptação direta. As almofadas demister em malha de arame tricotada criam caminhos tortuosos com elevada porosidade volumétrica, permitindo a drenagem gravitacional contínua com perda de carga reduzida em velocidades operacionais nominais.",
        "Para a retenção de aerossóis submicrométricos ou cargas de fluxo severas, fornecemos placas e cilindros sinterizados multicamadas com estrutura porosa rígida e estável. Todos os elementos são manufaturados em aços inoxidáveis passivados AISI 304 e 316L, assegurando alta durabilidade contra vapores agressivos e rotinas periódicas de higienização CIP.",
      ],
      keyPoints: [
        "Impacto inercial e coalescência de filamentos capturam gotículas líquidas de 1 µm a 50 µm",
        "Alta fração de vazios garante baixa perda de carga em velocidades superficiais de 1,5 a 3,8 m/s",
        "Aços inoxidáveis AISI 304 e 316L passivados resistentes a vapores agressivos e limpezas industriais",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-knitted-sta-a66f45.png",
        alt: "Elemento demister de malha tricotada de aço inoxidável para eliminação de névoas",
      },
    },
    specTable: {
      heading: "Configurações de Meios para Equipamentos de Separação Gás-Líquido",
      intro: "Meios filtrantes metálicos e eliminadores de névoa desenvolvidos para lavadores industriais, tambores de purga e vasos de separação.",
      columns: [
        { key: "service", label: "Aplicação do Processo", align: "left" },
        { key: "construction", label: "Construção do Meio", align: "left" },
        { key: "retention", label: "Faixa de Gotículas Alvo", align: "right" },
        { key: "alloys", label: "Ligas Padrão", align: "right" },
        { key: "drainage", label: "Mecanismo de Drenagem", align: "right" },
      ],
      rows: [
        {
          service: "Lavadores Convencionais & Vasos Knockout",
          construction: "Almofada demister de malha tricotada com grade superior e inferior",
          retention: "5 µm – 50 µm",
          alloys: "AISI 304 / 316L",
          drainage: "Drenagem por gravidade contra a corrente de gás",
        },
        {
          service: "Coalescência de Aerossóis Finos",
          construction: "Almofada multifilamento com gradiente de densidade",
          retention: "2 µm – 10 µm",
          alloys: "AISI 316L",
          drainage: "Coalescência entre camadas e calhas perimétricas",
        },
        {
          service: "Vapores Agressivos & Alta Carga Dinâmica",
          construction: "Placa ou cilindro sinterizado de 5 camadas por difusão",
          retention: "1 µm – 20 µm",
          alloys: "AISI 316L",
          drainage: "Ação capilar e ranhuras calibradas de dreno",
        },
        {
          service: "Separação de Chuva e Gotas Grossas na Entrada",
          construction: "Tela tecida pesada de malha quadrada com reforços soldados",
          retention: "20 µm – 100 µm",
          alloys: "AISI 304 / 316L",
          drainage: "Escoamento laminar para calha coletora",
        },
      ],
      caption: "Diâmetros, espessuras de almofada, bitolas de arame e grades de montagem são customizados conforme o projeto mecânico.",
    },
    features: {
      heading: "Destaques Técnicos e Operacionais",
      intro: "Nossos meios de separação reúnem alta eficiência de coleta de gotas, estabilidade dimensional e resistência à corrosão química.",
      items: [
        {
          title: "Caminho Tortuoso Multifilamento",
          text: "Malhas entrelaçadas criam múltiplos pontos de impacto inercial, promovendo rápida coalescência sem restringir o fluxo de gás.",
        },
        {
          title: "Elevada Fração de Vazios & Baixo Delta P",
          text: "Geometria volumétrica aberta que permite vazão suave entre 1,5 e 3,8 m/s, eliminando quedas de pressão excessivas no sistema.",
        },
        {
          title: "Reforço Estrutural Robusto",
          text: "Grades soldadas superiores e inferiores que sustentam a almofada contra pulsos de vazão, prevenindo desvios periféricos.",
        },
        {
          title: "Resistência à Corrosão Química",
          text: "Aços austeníticos passivados toleram vapores com cloretos, condensados ácidos e lavagens CIP rotineiras sem perda de integridade.",
        },
      ],
    },
    related: {
      heading: "Aplicações e Produtos Relacionados",
      intro: "Conheça soluções integradas em peneiramento, clarificação de suspensões e telas industriais de aço inoxidável.",
      cards: [
        {
          title: "Peneiramento & Separação",
          text: "Soluções completas em meios metálicos para classificação de partículas, desaguamento de polpas e separação de gases.",
          to: "/ScreeningAndSeparationHubPage",
          badge: "Aplicação Principal",
          linkLabel: "Ver Visão Geral",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sieve_bend_screen.webp",
            alt: "Visão geral de peneiramento e separação com meios metálicos",
          },
        },
        {
          title: "Separação Sólido-Líquido",
          text: "Telas de arame em cunha com ranhura contínua e telas tecidas para desidratação de polpas e clarificação industrial.",
          to: "/SolidLiquidSeparationPage",
          badge: "Aplicação Irmã",
          linkLabel: "Explorar Sólido-Líquido",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-eaf99e.png",
            alt: "Painel de desidratação em arame perfilado em cunha com ranhuras de precisão",
          },
        },
        {
          title: "Peneiramento de Pós e Partículas",
          text: "Telas tecidas de malha quadrada de precisão e aros tensionados para classificação vibratória de alta frequência.",
          to: "/PowderAndParticleScreeningPage",
          badge: "Aplicação Irmã",
          linkLabel: "Explorar Peneiramento",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-83c744.png",
            alt: "Tela de arame de aço inoxidável para classificação de pós e partículas",
          },
        },
        {
          title: "Telas de Arame Tecidas",
          text: "Telas metálicas de precisão conforme ISO 9044 e ASTM E2016 para aplicações industriais críticas de separação.",
          to: "/WovenWireMeshPage",
          badge: "Produto Recomendado",
          linkLabel: "Explorar Telas Tecidas",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-83c744.png",
            alt: "Tela de arame tecida de aço inoxidável para separação e peneiramento industrial",
          },
        },
        {
          title: "Componentes Filtrantes",
          text: "Demisters cilíndricos, tubos soldados e elementos multicamadas customizados para vasos e colunas de processo.",
          to: "/FilterComponentsPage",
          badge: "Produto Recomendado",
          linkLabel: "Explorar Componentes",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-an-industria-3baee0.png",
            alt: "Tubos e cilindros filtrantes soldados e sinterizados em aço inoxidável",
          },
        },
      ],
    },
    cta: {
      id: "gas-liquid-quote",
      eyebrow: "Consulta Técnica",
      heading: "Solicite uma Cotação para Eliminadores de Névoa Gás-Líquido",
      lead: "Informe o diâmetro do vaso, faixa de retenção de gotículas, velocidade superficial do gás, liga metálica e fixação mecânica para análise técnica.",
      secondaryLabel: "Filtração de gás e ar",
      secondaryTo: "/GasAndAirFiltrationPage",
      note: "Relatórios de ensaio de material (MTR) e análise PMI disponíveis quando especificados no pedido.",
    },
  },
};
